// /event-day — live entree-dashboard, geoptimaliseerd voor groot scherm in de zaal.
// Auto-refresh elke 5 sec. Geen sidebar (eigen layout, fullscreen).
//
// Toont:
//  - Grote counter "X / Y binnen" met progress-balk
//  - Sparkline van scans per 5 min over laatste 2 uur
//  - Recent-scans strip (laatste 10 scans met naam + tijd)
//  - Live klok + tijd tot/sinds eventstart

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { supabase } from "@/lib/supabase";
import { useCurrentEvent } from "@/lib/event-context";

const REFRESH_MS = 5000;
const HISTORY_HOURS = 2;
const BUCKET_MIN = 5;

interface RecentScan {
  ticket_id: string | null;
  scanned_at: string;
  ticket: {
    ticket_no: string;
    attendee: { first_name: string | null; last_name: string | null } | null;
    ticket_type: { name: string | null } | null;
  } | null;
}

async function fetchEventDayData(eventId: string) {
  const sinceHist = new Date(Date.now() - HISTORY_HOURS * 3600_000).toISOString();

  const [
    { count: totalIssued },
    { count: scannedCount },
    { data: recent },
    { data: bucketRows },
  ] = await Promise.all([
    // Totaal uitgegeven (excl. void)
    supabase.from("tickets").select("id", { head: true, count: "exact" }).neq("status", "void"),
    // Aantal gescand
    supabase.from("tickets").select("id", { head: true, count: "exact" }).not("checked_in_at", "is", null),
    // Laatste 10 succesvolle scans (uit scan_logs)
    supabase
      .from("scan_logs")
      .select("ticket_id, scanned_at, ticket:tickets(ticket_no, attendee:attendees(first_name, last_name), ticket_type:ticket_types(name))")
      .eq("result", "ok")
      .order("scanned_at", { ascending: false })
      .limit(10),
    // Tempo: alle ok-scans van laatste HISTORY_HOURS uur (client groepeert in buckets)
    supabase
      .from("scan_logs")
      .select("scanned_at")
      .eq("result", "ok")
      .gte("scanned_at", sinceHist)
      .order("scanned_at", { ascending: true }),
  ]);

  const _ = eventId; // event_id zit niet op tickets/scan_logs maar op orders — voor v1 single-event accepteren we dit
  void _;

  // Bucketize scans
  const buckets = new Map<number, number>();
  const bucketMs = BUCKET_MIN * 60_000;
  const nowFloor = Math.floor(Date.now() / bucketMs) * bucketMs;
  const totalBuckets = (HISTORY_HOURS * 60) / BUCKET_MIN;
  for (let i = 0; i < totalBuckets; i++) {
    buckets.set(nowFloor - i * bucketMs, 0);
  }
  for (const r of (bucketRows ?? []) as Array<{ scanned_at: string }>) {
    const t = Math.floor(new Date(r.scanned_at).getTime() / bucketMs) * bucketMs;
    if (buckets.has(t)) buckets.set(t, (buckets.get(t) ?? 0) + 1);
  }
  const tempo = Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([t, n]) => ({ t, n }));

  return {
    totalIssued: totalIssued ?? 0,
    scanned: scannedCount ?? 0,
    recent: (recent ?? []) as unknown as RecentScan[],
    tempo,
  };
}

export function EventDay() {
  const { data: event } = useCurrentEvent();
  const [now, setNow] = useState(() => Date.now());

  // Live klok
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["event-day", event?.id],
    queryFn: () => fetchEventDayData(event!.id),
    enabled: !!event?.id,
    refetchInterval: REFRESH_MS,
  });

  if (!event) return <div className="empty"><span className="spinner" /> Event laden…</div>;

  const capacity = event.capacity ?? 600;
  const total = data?.totalIssued ?? 0;
  const scanned = data?.scanned ?? 0;
  const pctScanned = total > 0 ? (scanned / total) * 100 : 0;
  const pctSold = (total / capacity) * 100;

  const startMs = new Date(event.starts_at).getTime();
  const diffMin = Math.round((startMs - now) / 60000);
  const eventStatus =
    diffMin > 60 ? `Start over ${Math.round(diffMin / 60)}u${Math.abs(diffMin % 60)}m`
      : diffMin > 0 ? `Start over ${diffMin} min`
      : diffMin > -60 ? `Live · ${-diffMin} min open`
      : `Live · ${Math.round(-diffMin / 60)}u${Math.abs(-diffMin % 60)}m open`;

  // Sparkline schaling
  const tempo = data?.tempo ?? [];
  const maxN = Math.max(1, ...tempo.map((b) => b.n));
  const W = 100, H = 100;
  const path = tempo.map((b, i) => {
    const x = (i / Math.max(1, tempo.length - 1)) * W;
    const y = H - (b.n / maxN) * H;
    return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  const totalLastHour = tempo.slice(-12).reduce((s, b) => s + b.n, 0); // 12 buckets × 5 min = 60 min

  return (
    <div className="event-day">
      <header className="ed-head">
        <div>
          <div className="ed-event">{event.name}</div>
          <div className="ed-status">{eventStatus} · {dayjs(now).format("HH:mm:ss")}</div>
        </div>
        <Link to="/" className="pill">← Admin</Link>
      </header>

      <div className="ed-counter">
        <div className="ed-big">
          {scanned}<span className="ed-sep">/</span><span className="ed-of">{total}</span>
        </div>
        <div className="ed-lbl">binnen</div>
        <div className="ed-bar">
          <div className="ed-bar-fill" style={{ width: `${Math.min(100, pctScanned)}%` }} />
        </div>
        <div className="ed-meta">
          {pctScanned.toFixed(0)}% gescand · {total} verkocht ({pctSold.toFixed(0)}% van {capacity})
        </div>
      </div>

      <div className="ed-grid">
        <div className="ed-card">
          <div className="ed-card-h">
            <div className="ed-card-t">★ Tempo (laatste {HISTORY_HOURS}u, per {BUCKET_MIN} min)</div>
            <div className="ed-card-v">{totalLastHour}<small> /uur</small></div>
          </div>
          <div className="ed-spark">
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
              <defs>
                <linearGradient id="sparkfill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="var(--c-orange)" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="var(--c-orange)" stopOpacity="0" />
                </linearGradient>
              </defs>
              {tempo.length > 1 && (
                <>
                  <path d={`${path} L${W},${H} L0,${H} Z`} fill="url(#sparkfill)" />
                  <path d={path} fill="none" stroke="var(--c-orange)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                </>
              )}
            </svg>
          </div>
        </div>

        <div className="ed-card">
          <div className="ed-card-h">
            <div className="ed-card-t">★ Recent gescand</div>
          </div>
          {isLoading && <div className="empty">Laden…</div>}
          {!isLoading && (data?.recent ?? []).length === 0 && (
            <div className="empty">Nog geen scans.</div>
          )}
          <ul className="ed-recent">
            {(data?.recent ?? []).map((r, i) => {
              const a = r.ticket?.attendee;
              const name = `${a?.first_name ?? ""} ${a?.last_name ?? ""}`.trim() || "Gast";
              return (
                <li key={`${r.ticket_id}-${r.scanned_at}-${i}`}>
                  <span className="ed-r-name">{name}</span>
                  <span className="ed-r-type">{r.ticket?.ticket_type?.name ?? ""}</span>
                  <span className="ed-r-time">{dayjs(r.scanned_at).format("HH:mm:ss")}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <footer className="ed-foot">
        Auto-refresh elke {REFRESH_MS / 1000}s · Open op groot scherm voor entrée-team
      </footer>
    </div>
  );
}
